// Client-side twin of the backend's FindMetrics (mappingaisuggest/source.go):
// in a fetched document that holds many metrics (SEC companyfacts:
// facts.us-gaap.<Concept>.units.USD), find every list shaped like the one a
// mapping already reads. Used by the editor's "add all metrics" panel, which has
// the (pruned) document in hand but no AI round trip.
import type { AIMetric } from '../api/dataApi';
import { resolvePath, type JsonValue } from './mappingEngine';

export const MAX_SIBLING_METRICS = 750;

const camelBreak = /([a-z0-9])([A-Z])/g;
export function snakeName(s: string): string {
  return s.replace(camelBreak, '$1_$2').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

const isObj = (v: JsonValue | undefined): v is { [k: string]: JsonValue } => v !== null && typeof v === 'object' && !Array.isArray(v);

// Every list of objects in the document (dotted path -> its first record).
export function listsIn(doc: JsonValue): { path: string; first: JsonValue }[] {
  const out: { path: string; first: JsonValue }[] = [];
  const walk = (v: JsonValue, prefix: string, depth: number) => {
    if (depth > 8 || out.length > 5000) return;
    if (Array.isArray(v)) {
      if (prefix && isObj(v[0])) out.push({ path: prefix, first: v[0] });
      return;
    }
    if (isObj(v)) for (const [k, c] of Object.entries(v)) if (!k.includes('.')) walk(c, prefix ? `${prefix}.${k}` : k, depth + 1);
  };
  walk(doc, '', 0);
  return out;
}

const numeric = (v: JsonValue | undefined) => typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)));

// valueFrom: the record field the mapping's Value rule reads - a sibling whose
// records hold no number there does not fit and is left out. The list at repPath
// comes first. Fewer than 3 matches = not a many-metrics document ([]).
export function findSiblingMetrics(doc: JsonValue, repPath: string, valueFrom?: string, max = MAX_SIBLING_METRICS): AIMetric[] {
  const rep = repPath.split('.');
  const n = rep.length;
  const lists = listsIn(doc).map((l) => ({ ...l, segs: l.path.split('.') }));
  const matches = (segs: string[], metricPos: number, unitPos: number) =>
    segs.length === n && segs.every((s, j) => j === metricPos || j === unitPos || s === rep[j]);
  let bestPos = -1;
  let bestCount = 0;
  for (let pos = 0; pos < n; pos++) {
    const count = lists.filter((l) => matches(l.segs, pos, -1)).length;
    if (count > bestCount) {
      bestPos = pos;
      bestCount = count;
    }
  }
  if (bestPos < 0 || bestCount < 3) return [];
  const unitPos = bestPos !== n - 1 && n >= 2 ? n - 1 : -1;
  const out: AIMetric[] = [];
  const add = (path: string, segs: string[], first: JsonValue) => {
    const name = snakeName(segs[bestPos]);
    if (!name) return;
    if (valueFrom && !numeric(resolvePath(first, valueFrom))) return;
    out.push({ path, name, ...(unitPos >= 0 ? { unit: segs[unitPos] } : {}) });
  };
  const repList = lists.find((l) => l.path === repPath);
  if (repList) add(repPath, rep, repList.first);
  for (const l of lists) {
    if (out.length >= max) break;
    if (l.path !== repPath && matches(l.segs, bestPos, unitPos)) add(l.path, l.segs, l.first);
  }
  return out.length >= 3 ? out : [];
}
